import { useEffect, useRef, useState } from 'react'
import JSZip from 'jszip'
import {
  ArrowLeft,
  CheckCircle2,
  Download,
  ImagePlus,
  LoaderCircle,
  LockKeyhole,
  Plus,
  Trash2,
  UploadCloud,
  X,
} from 'lucide-react'
import type { ImageItem, ProcessedImage, ToolDefinition, ToolOptions } from '../types'
import { defaultToolOptions } from '../types'
import { processImage, readImageSize } from '../utils/imageProcessor'
import { Logo } from './Logo'
import { HtmlWorkspace } from './HtmlWorkspace'
import { ToolOptionsPanel } from './ToolOptionsPanel'
import { WorkbenchIcon } from './WorkbenchIcon'

interface WorkspaceProps {
  tool: ToolDefinition
  onClose: () => void
}

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
}

function saveBlob(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = name
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  window.setTimeout(() => URL.revokeObjectURL(url), 1_000)
}

export function Workspace(props: WorkspaceProps) {
  return props.tool.id === 'html-to-image' ? <HtmlWorkspace {...props} /> : <ImageWorkspace {...props} />
}

function ImageWorkspace({ tool, onClose }: WorkspaceProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const itemsRef = useRef<ImageItem[]>([])
  const resultsRef = useRef<ProcessedImage[]>([])
  const [items, setItems] = useState<ImageItem[]>([])
  const [results, setResults] = useState<ProcessedImage[]>([])
  const [options, setOptions] = useState<ToolOptions>({ ...defaultToolOptions })
  const [dragging, setDragging] = useState(false)
  const [loadingFiles, setLoadingFiles] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [zipping, setZipping] = useState(false)
  const [progress, setProgress] = useState(0)
  const [message, setMessage] = useState('')

  itemsRef.current = items
  resultsRef.current = results

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !processing) onClose()
    }
    window.addEventListener('keydown', closeOnEscape)
    document.body.classList.add('workspace-open')
    return () => {
      window.removeEventListener('keydown', closeOnEscape)
      document.body.classList.remove('workspace-open')
    }
  }, [onClose, processing])

  useEffect(() => {
    return () => {
      itemsRef.current.forEach((item) => URL.revokeObjectURL(item.preview))
      resultsRef.current.forEach((result) => URL.revokeObjectURL(result.url))
    }
  }, [])

  const clearResults = () => {
    results.forEach((result) => URL.revokeObjectURL(result.url))
    setResults([])
    setProgress(0)
  }

  const addFiles = async (incoming: FileList | File[]) => {
    const candidates = Array.from(incoming).filter((file) => file.type.startsWith('image/'))
    if (!candidates.length) {
      setMessage('이미지 파일이 아니다. JPG, PNG, WEBP 같은 파일로 다시 넣어줘.')
      return
    }
    const available = Math.max(0, 30 - items.length)
    const accepted = candidates.slice(0, available).filter((file) => file.size <= 50 * 1024 * 1024)
    if (!accepted.length) {
      setMessage('한 장은 50MB까지, 한 번에는 30장까지 받는다.')
      return
    }

    setLoadingFiles(true)
    setMessage('')
    clearResults()
    const loaded: ImageItem[] = []
    for (const file of accepted) {
      try {
        const size = await readImageSize(file)
        loaded.push({
          id: crypto.randomUUID(),
          file,
          preview: URL.createObjectURL(file),
          width: size.width,
          height: size.height,
        })
      } catch {
        setMessage(`${file.name} 파일은 읽지 못했다. 다른 이미지로 다시 넣어줘.`)
      }
    }
    setItems((current) => [...current, ...loaded])
    if (tool.id === 'crop' && items.length === 0 && loaded[0]) {
      setOptions((current) => ({
        ...current,
        cropWidth: loaded[0].width,
        cropHeight: loaded[0].height,
      }))
    }
    setLoadingFiles(false)
  }

  const removeItem = (id: string) => {
    const item = items.find((candidate) => candidate.id === id)
    if (item) URL.revokeObjectURL(item.preview)
    setItems((current) => current.filter((candidate) => candidate.id !== id))
    clearResults()
  }

  const clearAll = () => {
    items.forEach((item) => URL.revokeObjectURL(item.preview))
    setItems([])
    clearResults()
    setMessage('')
  }

  const runProcess = async () => {
    if (!items.length || processing) return
    clearResults()
    setProcessing(true)
    setMessage('')
    const nextResults: ProcessedImage[] = []
    let failed = 0

    for (let index = 0; index < items.length; index += 1) {
      const item = items[index]
      try {
        const processed = await processImage(item.file, tool.id, options)
        nextResults.push({
          id: crypto.randomUUID(),
          sourceId: item.id,
          name: processed.name,
          blob: processed.blob,
          url: URL.createObjectURL(processed.blob),
          width: processed.width,
          height: processed.height,
          originalSize: item.file.size,
        })
      } catch (error) {
        failed += 1
        setMessage(error instanceof Error ? error.message : '작업이 멈췄다. 설정을 확인하고 다시 돌려줘.')
      }
      setProgress(Math.round(((index + 1) / items.length) * 100))
      await new Promise((resolve) => window.setTimeout(resolve, 30))
    }

    setResults(nextResults)
    setProcessing(false)
    if (!failed) setMessage(`끝. ${nextResults.length}장 처리했다.`)
    else if (nextResults.length) setMessage(`${nextResults.length}장은 끝냈고 ${failed}장은 처리하지 못했다.`)
  }

  const downloadAll = async () => {
    if (!results.length) return
    if (results.length === 1) {
      saveBlob(results[0].blob, results[0].name)
      return
    }
    setZipping(true)
    const zip = new JSZip()
    results.forEach((result) => zip.file(result.name, result.blob))
    const blob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE' })
    saveBlob(blob, `zozinimg-${tool.id}-${Date.now()}.zip`)
    setZipping(false)
  }

  const totalOriginal = results.reduce((sum, result) => sum + result.originalSize, 0)
  const totalResult = results.reduce((sum, result) => sum + result.blob.size, 0)
  const savedPercent = totalOriginal > 0 ? Math.max(0, Math.round((1 - totalResult / totalOriginal) * 100)) : 0

  return (
    <div className="workspace" role="dialog" aria-modal="true" aria-label={`${tool.title} 작업 화면`}>
      <header className="workspace-header">
        <Logo compact />
        <div className="workspace-title">
          <span className={`tool-mini-icon accent-${tool.accent}`}><WorkbenchIcon id={tool.id} size={22} /></span>
          <span><small>WORKBENCH OPEN</small><strong>{tool.title}</strong></span>
        </div>
        <button type="button" className="workspace-close" onClick={onClose} disabled={processing} aria-label="작업 화면 닫기">
          <X size={22} />
        </button>
      </header>

      <main className="workspace-main">
        <section className="workspace-copy">
          <button type="button" className="back-link" onClick={onClose} disabled={processing}>
            <ArrowLeft size={16} /> 작업대로 돌아가기
          </button>
          <p>WORK ORDER / 01</p>
          <h1>{tool.title}</h1>
          <span>{tool.description}</span>
        </section>

        {!items.length ? (
          <button
            type="button"
            className={`drop-zone hero-drop ${dragging ? 'dragging' : ''}`}
            onClick={() => inputRef.current?.click()}
            onDragEnter={(event) => { event.preventDefault(); setDragging(true) }}
            onDragOver={(event) => event.preventDefault()}
            onDragLeave={() => setDragging(false)}
            onDrop={(event) => {
              event.preventDefault()
              setDragging(false)
              void addFiles(event.dataTransfer.files)
            }}
          >
            <span className="upload-orbit"><UploadCloud size={38} /></span>
            <strong>{loadingFiles ? '파일을 읽고 있다.' : '파일을 작업대에 던져.'}</strong>
            <span>끌어놓거나 눌러서 선택 · 최대 30장</span>
            <em><ImagePlus size={18} /> 파일 가져오기</em>
            <small><LockKeyhole size={14} /> 서버 안 거침 · 기기 안에서 처리</small>
          </button>
        ) : (
          <div className="workspace-grid">
            <section className="file-stage">
              <div className="file-toolbar">
                <div>
                  <strong>{items.length}장 작업대에 올림</strong>
                  <span>{formatBytes(items.reduce((sum, item) => sum + item.file.size, 0))}</span>
                </div>
                <div>
                  <button type="button" className="secondary-button" onClick={() => inputRef.current?.click()} disabled={processing}>
                    <Plus size={16} /> 더 올리기
                  </button>
                  <button type="button" className="icon-button" onClick={clearAll} disabled={processing} title="모두 비우기">
                    <Trash2 size={17} />
                  </button>
                </div>
              </div>

              <div
                className={`file-grid ${dragging ? 'dragging' : ''}`}
                onDragEnter={(event) => { event.preventDefault(); setDragging(true) }}
                onDragOver={(event) => event.preventDefault()}
                onDragLeave={() => setDragging(false)}
                onDrop={(event) => {
                  event.preventDefault()
                  setDragging(false)
                  void addFiles(event.dataTransfer.files)
                }}
              >
                {items.map((item, index) => {
                  const result = results.find((candidate) => candidate.sourceId === item.id)
                  return (
                    <article className={`file-card ${result ? 'done' : ''}`} key={item.id}>
                      <div className="file-preview">
                        <img src={result?.url || item.preview} alt="" />
                        <span>{String(index + 1).padStart(2, '0')}</span>
                        {result && <i><CheckCircle2 size={18} /></i>}
                      </div>
                      <div className="file-meta">
                        <strong title={result?.name || item.file.name}>{result?.name || item.file.name}</strong>
                        <span>
                          {result ? `${result.width}×${result.height} · ${formatBytes(result.blob.size)}` : `${item.width}×${item.height} · ${formatBytes(item.file.size)}`}
                        </span>
                      </div>
                      {result ? (
                        <button type="button" className="file-action" onClick={() => saveBlob(result.blob, result.name)} title="이 파일 다운로드">
                          <Download size={16} />
                        </button>
                      ) : (
                        <button type="button" className="file-action" onClick={() => removeItem(item.id)} disabled={processing} title="파일 제거">
                          <X size={16} />
                        </button>
                      )}
                    </article>
                  )
                })}
              </div>
            </section>

            <ToolOptionsPanel tool={tool} options={options} onChange={(next) => { setOptions((current) => ({ ...current, ...next })); clearResults() }} />
          </div>
        )}

        {message && <div className={`workspace-message ${results.length ? 'success' : ''}`}>{message}</div>}
      </main>

      {!!items.length && (
        <footer className="action-dock">
          <div className="dock-status">
            {processing ? (
              <>
                <LoaderCircle className="spin" size={20} />
                <span><strong>공구 돌리는 중</strong><small>{progress}% 진행</small></span>
              </>
            ) : results.length ? (
              <>
                <CheckCircle2 size={21} />
                <span><strong>끝. {results.length}장 처리</strong><small>{tool.id === 'compress' ? `${savedPercent}% 덜어냄` : formatBytes(totalResult)}</small></span>
              </>
            ) : (
              <>
                <LockKeyhole size={20} />
                <span><strong>돌릴 준비 끝</strong><small>서버 업로드 없음</small></span>
              </>
            )}
          </div>
          {processing && <div className="progress-track"><span style={{ width: `${progress}%` }} /></div>}
          {results.length ? (
            <button type="button" className="primary-button download-button" onClick={() => void downloadAll()} disabled={zipping}>
              {zipping ? <LoaderCircle className="spin" size={19} /> : <Download size={19} />}
              {zipping ? 'ZIP 묶는 중' : results.length > 1 ? '끝난 파일 ZIP으로 받기' : '끝난 파일 받기'}
            </button>
          ) : (
            <button type="button" className="primary-button" onClick={() => void runProcess()} disabled={processing || loadingFiles}>
              {processing ? <LoaderCircle className="spin" size={19} /> : <span className="button-arrow">→</span>}
              {processing ? '공구 돌리는 중' : tool.action}
            </button>
          )}
        </footer>
      )}

      <input
        ref={inputRef}
        className="visually-hidden"
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml,image/bmp"
        multiple={tool.multiple}
        onChange={(event) => {
          if (event.target.files) void addFiles(event.target.files)
          event.target.value = ''
        }}
      />
    </div>
  )
}
